import {
  AfterViewInit,
  DestroyRef,
  Directive,
  ElementRef,
  Injector,
  OnDestroy,
  computed,
  inject,
  input,
} from '@angular/core'
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop'

import { ChatConfig, ChatHeaderDisplayMode } from '@flowx/angular-ui-toolkit'
import { theme } from '@flowx/core-sdk'
import { ThemeComponent, getComponentStyles } from '@flowx/core-theme'

import { FlxComponentThemeBaseDirective } from './component-theme-base.directive'

const chatDisplayTypeToComponentType: Record<ChatHeaderDisplayMode, ThemeComponent[]> = {
  ['floating']: [
    ThemeComponent.ChatFloating,
    ThemeComponent.ChatHeaderFloating,
    ThemeComponent.ChatMessagesFloating,
    ThemeComponent.ChatMessageActionsFloating,
    ThemeComponent.ChatMessageFeedbackFloating,
    ThemeComponent.ChatMessageFeedbackReasonFloating,
    ThemeComponent.ChatAgentMessageFloating,
    ThemeComponent.ChatUserMessageFloating,
    ThemeComponent.ChatErrorMessageFloating,
    ThemeComponent.ChatInputFloating,
    ThemeComponent.ChatInputAreaFloating,
    ThemeComponent.ChatHistoryFloating,
    ThemeComponent.ChatHistorySectionFloating,
    ThemeComponent.ChatSuggestedPromptsFloating,
    ThemeComponent.ChatSuggestedPromptsSectionFloating,
    ThemeComponent.ChatSuggestedPromptsItemFloating,
  ],
  ['fullscreen']: [
    ThemeComponent.ChatFullScreen,
    ThemeComponent.ChatHeaderFullScreen,
    ThemeComponent.ChatMessagesFullScreen,
    ThemeComponent.ChatMessageActionsFullScreen,
    ThemeComponent.ChatMessageFeedbackFullScreen,
    ThemeComponent.ChatMessageFeedbackReasonFullScreen,
    ThemeComponent.ChatAgentMessageFullScreen,
    ThemeComponent.ChatUserMessageFullScreen,
    ThemeComponent.ChatErrorMessageFullScreen,
    ThemeComponent.ChatInputFullScreen,
    ThemeComponent.ChatInputAreaFullScreen,
    ThemeComponent.ChatHistoryFullScreen,
    ThemeComponent.ChatHistorySectionFullScreen,
    ThemeComponent.ChatSuggestedPromptsFullScreen,
    ThemeComponent.ChatSuggestedPromptsSectionFullScreen,
    ThemeComponent.ChatSuggestedPromptsItemFullScreen,
  ],
  ['fill']: [
    ThemeComponent.Chat,
    ThemeComponent.ChatHeader,
    ThemeComponent.ChatMessages,
    ThemeComponent.ChatMessageActions,
    ThemeComponent.ChatMessageFeedback,
    ThemeComponent.ChatMessageFeedbackReason,
    ThemeComponent.ChatAgentMessage,
    ThemeComponent.ChatUserMessage,
    ThemeComponent.ChatErrorMessage,
    ThemeComponent.ChatInput,
    ThemeComponent.ChatInputArea,
    ThemeComponent.ChatHistory,
    ThemeComponent.ChatHistorySection,
    ThemeComponent.ChatSuggestedPrompts,
    ThemeComponent.ChatSuggestedPromptsSection,
    ThemeComponent.ChatSuggestedPromptsItem,
  ],
}

const chatHeaderButtonToComponentType: Record<ChatHeaderDisplayMode, ThemeComponent> = {
  floating: ThemeComponent.ChatHeaderButtonFloating,
  fullscreen: ThemeComponent.ChatHeaderButtonFullScreen,
  fill: ThemeComponent.ChatHeaderButton,
}

const chatInputToComponentType: Record<ChatHeaderDisplayMode, ThemeComponent> = {
  floating: ThemeComponent.ChatInputFloating,
  fullscreen: ThemeComponent.ChatInputFullScreen,
  fill: ThemeComponent.ChatInput,
}

const chatHistorySectionToComponentType: Record<ChatHeaderDisplayMode, ThemeComponent> = {
  floating: ThemeComponent.ChatHistorySectionFloating,
  fullscreen: ThemeComponent.ChatHistorySectionFullScreen,
  fill: ThemeComponent.ChatHistorySection,
}

const chatHistoryItemToComponentType: Record<ChatHeaderDisplayMode, ThemeComponent> = {
  floating: ThemeComponent.ChatHistoryItemFloating,
  fullscreen: ThemeComponent.ChatHistoryItemFullScreen,
  fill: ThemeComponent.ChatHistoryItem,
}

const chatHistorySearchToComponentType: Record<ChatHeaderDisplayMode, ThemeComponent> = {
  floating: ThemeComponent.ChatHistorySearchFloating,
  fullscreen: ThemeComponent.ChatHistorySearchFullScreen,
  fill: ThemeComponent.ChatHistorySearch,
}

const chatUserVoiceMessageToComponentType: Record<ChatHeaderDisplayMode, ThemeComponent> = {
  floating: ThemeComponent.ChatUserVoiceMessageFloating,
  fullscreen: ThemeComponent.ChatUserVoiceMessageFullScreen,
  fill: ThemeComponent.ChatUserVoiceMessage,
}

const chatVoiceInputToComponentType: Record<ChatHeaderDisplayMode, ThemeComponent> = {
  floating: ThemeComponent.ChatVoiceInputFloating,
  fullscreen: ThemeComponent.ChatVoiceInputFullScreen,
  fill: ThemeComponent.ChatVoiceInput,
}

const chatInputFileAttachmentsToComponentType: Record<ChatHeaderDisplayMode, ThemeComponent> = {
  floating: ThemeComponent.ChatInputFileAttachmentsFloating,
  fullscreen: ThemeComponent.ChatInputFileAttachmentsFullScreen,
  fill: ThemeComponent.ChatInputFileAttachments,
}

const chatInputFileItemToComponentType: Record<ChatHeaderDisplayMode, ThemeComponent> = {
  floating: ThemeComponent.ChatInputFileItemFloating,
  fullscreen: ThemeComponent.ChatInputFileItemFullScreen,
  fill: ThemeComponent.ChatInputFileItem,
}

const chatUserFileAttachmentsToComponentType: Record<ChatHeaderDisplayMode, ThemeComponent> = {
  floating: ThemeComponent.ChatUserFileAttachmentsFloating,
  fullscreen: ThemeComponent.ChatUserFileAttachmentsFullScreen,
  fill: ThemeComponent.ChatUserFileAttachments,
}

const chatUserFileItemToComponentType: Record<ChatHeaderDisplayMode, ThemeComponent> = {
  floating: ThemeComponent.ChatUserFileItemFloating,
  fullscreen: ThemeComponent.ChatUserFileItemFullScreen,
  fill: ThemeComponent.ChatUserFileItem,
}

function parseClassNames(themeClassName: string | null | undefined): string[] {
  return themeClassName?.split(' ').filter(Boolean) ?? []
}

@Directive({
  selector: 'flx-chat',
  hostDirectives: [FlxComponentThemeBaseDirective],
  host: {
    '[class]': 'themeClassName',
    '[style.visibility]': 'visibility',
  },
})
export class FlxChatThemeDirective implements AfterViewInit, OnDestroy {
  #elementRef = inject(ElementRef<HTMLElement>).nativeElement
  #destroyRef = inject(DestroyRef)
  #injector = inject(Injector)
  #childObserver: MutationObserver | null = null

  #displayMode = computed(() => this.chatConfig()?.displayMode ?? 'fill')

  chatConfig = input<ChatConfig>()
  themeClassName = null
  visibility: string | null // used to delay render until all styles are applied

  ngAfterViewInit(): void {
    const buttonPreviousClasses: string[] = []
    const buttonHeaderPreviousClasses: string[] = []
    const textareaPreviousClasses: string[] = []
    const historySectionPreviousClasses: string[] = []
    const historyItemPreviousClasses: string[] = []
    const historySearchPreviousClasses: string[] = []
    const userVoiceMessagePreviousClasses: string[] = []
    const voiceInputPreviousClasses: string[] = []
    const inputFileAttachmentsPreviousClasses: string[] = []
    const userFileAttachmentsPreviousClasses: string[] = []
    const hostPreviousClasses: string[] = []

    // Apply theme classes to child elements that use modifier selectors (deep theming via direct
    // DOM manipulation). Modifier selectors (e.g. &.flx-chat-history-item:hover) are compound
    // selectors that require the emotion class to be on the same element — they don't work when
    // applied to an ancestor. Must re-query DOM each time since the view may change (chat <-> history).
    const applyDeepTheme = (displayMode: ChatHeaderDisplayMode): void => {
      const buttons = this.#elementRef.querySelectorAll(
        'button[flxButton]'
      ) as NodeListOf<HTMLButtonElement> | null
      const textarea = this.#elementRef.querySelector('flx-textarea') as HTMLTextAreaElement | null
      const historySections = this.#elementRef.querySelectorAll(
        '.flx-chat-history-section'
      ) as NodeListOf<HTMLElement> | null
      const historyItems = this.#elementRef.querySelectorAll(
        '.flx-chat-history-item'
      ) as NodeListOf<HTMLElement> | null

      if (buttons?.length) {
        const buttonClassNames = parseClassNames(getComponentStyles(ThemeComponent.Button, theme()))
        const buttonHeaderClassNames = parseClassNames(
          getComponentStyles(chatHeaderButtonToComponentType[displayMode], theme())
        )

        buttons.forEach((button) => {
          const initialVisibility = button.style.visibility
          button.style.visibility = 'hidden'

          if (button.classList.contains('flx-chat-header-button')) {
            button.classList.remove(...buttonHeaderPreviousClasses)
            if (buttonHeaderClassNames.length) {
              button.classList.add('flx-themed', ...buttonHeaderClassNames)
            }
          } else {
            button.classList.remove(...buttonPreviousClasses)
            if (buttonClassNames.length) {
              button.classList.add('flx-themed', ...buttonClassNames)
            }
          }

          button.style.visibility = initialVisibility || ''
        })

        buttonPreviousClasses.splice(0, buttonPreviousClasses.length, ...buttonClassNames)
        buttonHeaderPreviousClasses.splice(
          0,
          buttonHeaderPreviousClasses.length,
          ...buttonHeaderClassNames
        )
      }

      if (textarea) {
        const textareaClassNames = parseClassNames(
          getComponentStyles(chatInputToComponentType[displayMode], theme())
        )
        const initialVisibility = textarea.style.visibility
        textarea.style.visibility = 'hidden'
        textarea.classList.remove(...textareaPreviousClasses)
        if (textareaClassNames.length) {
          textarea.classList.add('flx-themed', ...textareaClassNames)
        }
        textarea.style.visibility = initialVisibility || ''
        textareaPreviousClasses.splice(0, textareaPreviousClasses.length, ...textareaClassNames)
      }

      if (historySections?.length) {
        const historySectionClassNames = parseClassNames(
          getComponentStyles(chatHistorySectionToComponentType[displayMode], theme())
        )

        historySections.forEach((section) => {
          section.classList.remove(...historySectionPreviousClasses)
          if (historySectionClassNames.length) {
            section.classList.add('flx-themed', ...historySectionClassNames)
          }
        })

        historySectionPreviousClasses.splice(
          0,
          historySectionPreviousClasses.length,
          ...historySectionClassNames
        )
      }

      if (historyItems?.length) {
        const historyItemClassNames = parseClassNames(
          getComponentStyles(chatHistoryItemToComponentType[displayMode], theme())
        )

        historyItems.forEach((item) => {
          item.classList.remove(...historyItemPreviousClasses)
          if (historyItemClassNames.length) {
            item.classList.add('flx-themed', ...historyItemClassNames)
          }
        })

        historyItemPreviousClasses.splice(
          0,
          historyItemPreviousClasses.length,
          ...historyItemClassNames
        )
      }

      // Modifier selectors (&.focused, &:hover) require the emotion class on the
      // search element itself — same reason as history items above.
      const historySearch = this.#elementRef.querySelector(
        '.flx-chat-history-search'
      ) as HTMLElement | null

      if (historySearch) {
        const historySearchClassNames = parseClassNames(
          getComponentStyles(chatHistorySearchToComponentType[displayMode], theme())
        )
        historySearch.classList.remove(...historySearchPreviousClasses)
        if (historySearchClassNames.length) {
          historySearch.classList.add('flx-themed', ...historySearchClassNames)
        }
        historySearchPreviousClasses.splice(
          0,
          historySearchPreviousClasses.length,
          ...historySearchClassNames
        )
      }

      const messagesContainer = this.#elementRef.querySelector(
        '.flx-chat-messages'
      ) as HTMLElement | null

      if (messagesContainer) {
        const userVoiceMessageClassNames = parseClassNames(
          getComponentStyles(chatUserVoiceMessageToComponentType[displayMode], theme())
        )
        messagesContainer.classList.remove(...userVoiceMessagePreviousClasses)
        if (userVoiceMessageClassNames.length) {
          messagesContainer.classList.add('flx-themed', ...userVoiceMessageClassNames)
        }
        userVoiceMessagePreviousClasses.splice(
          0,
          userVoiceMessagePreviousClasses.length,
          ...userVoiceMessageClassNames
        )
      }

      const chatInput = this.#elementRef.querySelector('flx-chat-input') as HTMLElement | null

      if (chatInput) {
        const voiceInputClassNames = parseClassNames(
          getComponentStyles(chatVoiceInputToComponentType[displayMode], theme())
        )
        chatInput.classList.remove(...voiceInputPreviousClasses)
        if (voiceInputClassNames.length) {
          chatInput.classList.add('flx-themed', ...voiceInputClassNames)
        }
        voiceInputPreviousClasses.splice(
          0,
          voiceInputPreviousClasses.length,
          ...voiceInputClassNames
        )
      }

      // Input-side file attachments: the container carries both the attachments-container vars and
      // the chip (file item) vars; the chips read them via the `& .flx-chat-file-item` deep selector.
      const inputFileAttachments = this.#elementRef.querySelector(
        '.flx-chat-input-attachments'
      ) as HTMLElement | null

      if (inputFileAttachments) {
        const inputFileAttachmentsClassNames = [
          ...parseClassNames(
            getComponentStyles(chatInputFileAttachmentsToComponentType[displayMode], theme())
          ),
          ...parseClassNames(
            getComponentStyles(chatInputFileItemToComponentType[displayMode], theme())
          ),
        ]
        inputFileAttachments.classList.remove(...inputFileAttachmentsPreviousClasses)
        if (inputFileAttachmentsClassNames.length) {
          inputFileAttachments.classList.add('flx-themed', ...inputFileAttachmentsClassNames)
        }
        inputFileAttachmentsPreviousClasses.splice(
          0,
          inputFileAttachmentsPreviousClasses.length,
          ...inputFileAttachmentsClassNames
        )
      }

      // Message-side file attachments: one container per sent message, each carrying the user
      // attachments-container vars and the chip vars.
      const userFileAttachments = this.#elementRef.querySelectorAll(
        '.flx-chat-user-file-attachments'
      ) as NodeListOf<HTMLElement> | null

      if (userFileAttachments?.length) {
        const userFileAttachmentsClassNames = [
          ...parseClassNames(
            getComponentStyles(chatUserFileAttachmentsToComponentType[displayMode], theme())
          ),
          ...parseClassNames(
            getComponentStyles(chatUserFileItemToComponentType[displayMode], theme())
          ),
        ]

        userFileAttachments.forEach((container) => {
          container.classList.remove(...userFileAttachmentsPreviousClasses)
          if (userFileAttachmentsClassNames.length) {
            container.classList.add('flx-themed', ...userFileAttachmentsClassNames)
          }
        })

        userFileAttachmentsPreviousClasses.splice(
          0,
          userFileAttachmentsPreviousClasses.length,
          ...userFileAttachmentsClassNames
        )
      }
    }

    // Apply host theme classes via Angular's [class] binding.
    // Only called from toObservable (inside NgZone) so Angular picks up the change.
    const applyHostTheme = (displayMode: ChatHeaderDisplayMode): void => {
      const initialVisibility = this.#elementRef.style.visibility
      this.visibility = 'hidden'
      this.#elementRef.classList.remove(...hostPreviousClasses)

      const styleClasses = chatDisplayTypeToComponentType[displayMode].flatMap((componentType) =>
        parseClassNames(getComponentStyles(componentType, theme()))
      )
      this.themeClassName = `flx-themed ${styleClasses.join(' ')}`.trim()
      hostPreviousClasses.splice(0, hostPreviousClasses.length, ...styleClasses)
      this.visibility = initialVisibility
    }

    toObservable(this.#displayMode, { injector: this.#injector })
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((displayMode) => {
        applyHostTheme(displayMode)
        applyDeepTheme(displayMode)
      })

    // Observe child changes (including subtree) to re-apply deep theming when the view switches
    // (chat <-> history) or when elements are dynamically added (e.g. history sections/items from
    // async data). Only re-themes child elements — host classes are managed by Angular's [class]
    // binding and must not be touched outside NgZone.
    this.#childObserver = new MutationObserver(() => applyDeepTheme(this.#displayMode()))
    this.#childObserver.observe(this.#elementRef, { childList: true, subtree: true })
  }

  ngOnDestroy(): void {
    this.#childObserver?.disconnect()
    this.#childObserver = null
  }
}
